import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { safeFetch, safeFetchSingle } from '@/lib/safeFetch';

export type ThemeMode = 'light' | 'dark' | 'auto';
export type AccentColor = 'gold' | 'blue' | 'emerald' | 'slate';

export interface UserPreferences {
  themeMode: ThemeMode;
  accentColor: AccentColor;
}

const DEFAULT_PREFERENCES: UserPreferences = {
  themeMode: 'auto',
  accentColor: 'blue'
};

const LOCAL_STORAGE_KEY = 'userPreferences';

// Color mapping for CSS variables
const ACCENT_COLORS = {
  gold: {
    primary: '#FFD700',
    primaryForeground: '#000000',
    primaryHover: '#E6C200',
    accent: '#FFD700',
    accentForeground: '#000000'
  },
  blue: {
    primary: '#2563eb',
    primaryForeground: '#ffffff',
    primaryHover: '#1d4ed8',
    accent: '#2563eb',
    accentForeground: '#ffffff'
  },
  emerald: {
    primary: '#10b981',
    primaryForeground: '#ffffff',
    primaryHover: '#059669',
    accent: '#10b981',
    accentForeground: '#ffffff'
  },
  slate: {
    primary: '#64748b',
    primaryForeground: '#ffffff',
    primaryHover: '#475569',
    accent: '#64748b',
    accentForeground: '#ffffff'
  }
};

export function useUserPreferences() {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState<UserPreferences>(DEFAULT_PREFERENCES);
  const [isLoading, setIsLoading] = useState(true);

  // Apply theme to document
  const applyTheme = useCallback((themeMode: ThemeMode, accentColor: AccentColor) => {
    const root = document.documentElement;
    
    // Apply theme mode
    root.classList.remove('light', 'dark');
    
    if (themeMode === 'auto') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      root.classList.add(prefersDark ? 'dark' : 'light');
    } else {
      root.classList.add(themeMode);
    }

    // Apply accent colors using Tailwind CSS variable names
    const colorVars = ACCENT_COLORS[accentColor];
    
    // Set CSS custom properties for accent color
    root.style.setProperty('--primary', colorVars.primary);
    root.style.setProperty('--primary-foreground', colorVars.primaryForeground);
    root.style.setProperty('--accent', colorVars.accent);
    root.style.setProperty('--accent-foreground', colorVars.accentForeground);
    
    // Also set the hover variant
    root.style.setProperty('--primary-hover', colorVars.primaryHover);
  }, []);

  // Load preferences from storage/database
  const loadPreferences = useCallback(async () => {
    try {
      setIsLoading(true);

      // Try to load from Supabase first (if user is logged in)
      let loadedPreferences = DEFAULT_PREFERENCES;

      if (user?.id) {
        try {
          // Use regular select first to avoid 406 errors with .single()
          const results = await safeFetch<{ theme_mode: string | null; accent_color: string | null }>(
            supabase
              .from('user_preferences')
              .select('theme_mode, accent_color')
              .eq('user_id', user.id)
              .limit(1)
          );

          // Get the first result if any exist
          const data = Array.isArray(results) && results.length > 0 ? results[0] : null;

          if (data) {
            loadedPreferences = {
              themeMode: (data.theme_mode as ThemeMode) || DEFAULT_PREFERENCES.themeMode,
              accentColor: (data.accent_color as AccentColor) || DEFAULT_PREFERENCES.accentColor
            };
          }
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.debug('user_preferences query failed, using defaults:', error);
          }
          // Continue with DEFAULT_PREFERENCES
        }
      }

      // Fallback to localStorage if Supabase fails or no user
      if (loadedPreferences === DEFAULT_PREFERENCES) {
        try {
          const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
          if (stored) {
            const parsed = JSON.parse(stored);
            loadedPreferences = {
              themeMode: parsed.themeMode || DEFAULT_PREFERENCES.themeMode,
              accentColor: parsed.accentColor || DEFAULT_PREFERENCES.accentColor
            };
          }
        } catch (error) {
          console.warn('Failed to parse user preferences from localStorage', error);
        }
      }

      setPreferences(loadedPreferences);
      applyTheme(loadedPreferences.themeMode, loadedPreferences.accentColor);
    } catch (error) {
      console.error('Error loading user preferences:', error);
      setPreferences(DEFAULT_PREFERENCES);
      applyTheme(DEFAULT_PREFERENCES.themeMode, DEFAULT_PREFERENCES.accentColor);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, applyTheme]);

  // Save preferences to storage/database
  const savePreferences = useCallback(async (newPreferences: UserPreferences) => {
    try {
      setPreferences(newPreferences);
      applyTheme(newPreferences.themeMode, newPreferences.accentColor);

      // Save to Supabase if user is logged in
      if (user?.id) {
        try {
          const { error } = await supabase
            .from('user_preferences')
            .upsert({
              user_id: user.id,
              theme_mode: newPreferences.themeMode,
              accent_color: newPreferences.accentColor,
              updated_at: new Date().toISOString()
            });

          if (error) {
            console.debug('Failed to save to Supabase, saving to localStorage instead:', error);
            throw error;
          }
          return;
        } catch (error) {
          console.debug('user_preferences table not available, saving to localStorage');
        }
      }

      // Fallback to localStorage
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(newPreferences));
    } catch (error) {
      console.error('Error saving user preferences:', error);
      // Still try to save to localStorage as backup
      try {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(newPreferences));
      } catch (localError) {
        console.error('Failed to save to localStorage:', localError);
      }
    }
  }, [user?.id, applyTheme]);

  // Reset to defaults
  const resetPreferences = useCallback(() => {
    savePreferences(DEFAULT_PREFERENCES);
  }, [savePreferences]);

  // Listen for system theme changes when in auto mode
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (preferences.themeMode === 'auto') {
        applyTheme('auto', preferences.accentColor);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [preferences.themeMode, preferences.accentColor, applyTheme]);

  // Load preferences on mount
  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  return {
    preferences,
    isLoading,
    savePreferences,
    resetPreferences,
    updateThemeMode: (themeMode: ThemeMode) => 
      savePreferences({ ...preferences, themeMode }),
    updateAccentColor: (accentColor: AccentColor) => 
      savePreferences({ ...preferences, accentColor })
  };
}
